import React, { useState } from "react";
import { Lock, Crown, Plane } from "lucide-react";
import AvatarLayered from "../AvatarLayered";
import { wrestlerAvatar } from "../../lib/api";
import { Flag, fmtFans, fmtAmt } from "../../lib/tour";
import { TourClaimPops } from "./TourClaimPops";
import { useInZoom } from "../MobileZoom";

function PoolBar({ loc, inZoom }) {
  const isMG = !!loc.daily;
  const pct = isMG ? (loc.progress / loc.progress_needed) * 100 : (loc.pool / loc.cap) * 100;
  const empty = !isMG && loc.pool <= 0;
  return (
    <div className="w-full">
      <div className="h-1 rounded-full bg-black/70 border border-white/10 overflow-hidden">
        <div className="h-full transition-[width] duration-700"
             style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: empty ? "#FF007F" : isMG ? "#00F0FF" : "linear-gradient(90deg,#39FF14,#FFE600)" }} />
      </div>
      <div className={`${inZoom ? "flex" : "hidden md:flex"} items-center justify-center gap-1 mt-0.5 font-mono text-[8px] leading-none text-white/90`}>
        {isMG ? (
          <><Flag c={loc.currency} className="w-2.5 h-2.5" />{loc.done_today ? "Done today" : `${Math.floor(loc.progress / 60)}/60 min`}</>
        ) : (
          <><Flag c={loc.currency} className="w-3 h-2" /><span className={empty ? "text-[#FF007F]" : ""}>{fmtFans(loc.pool)}/{fmtFans(loc.cap)}</span>
            {loc.hit_zero && <Crown className="w-2.5 h-2.5 text-[#FFD700] shrink-0" style={{ filter: "drop-shadow(0 0 3px #FFD700)" }}
                                    data-testid={`tour-location-crown-${loc.id}`} aria-label="Emptied today: level-up at daily reset" />}</>
        )}
      </div>
    </div>
  );
}

function PlacedWrestler({ wrestler, selected, onDragStart }) {
  const active = wrestler.is_active;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, wrestler.wrestler_id)} data-testid={`tour-placed-${wrestler.wrestler_id}`} data-selected={selected ? "true" : "false"}
         className={`absolute left-1/2 -translate-x-1/2 bottom-0 z-20 w-[58%] aspect-square rounded-md overflow-hidden border-2 cursor-grab ${active && !selected ? "tour-active-ring" : ""}`}
         style={{ borderColor: selected ? "#FFE600" : active ? "#39FF14" : "#00F0FF", boxShadow: selected ? "0 0 14px #FFE600" : active ? undefined : "0 0 8px #00F0FF88" }}>
      <AvatarLayered fgSrc={wrestlerAvatar(wrestler)} promo={wrestler.owner_promotion} alt={wrestler.name} className="w-full h-full" />
      <span className="absolute bottom-0 left-0 bg-black/75 rounded-tr p-[1px]"><Lock className="w-2 h-2 text-[#FFE600]" /></span>
    </div>
  );
}

export function TourLocation({ loc, wrestler, floats, pops, armed, selected, onDrop, onClick, onDragStartPlaced }) {
  const [over, setOver] = useState(false);
  const inZoom = useInZoom();
  const active = wrestler?.is_active;
  const lastDrop = [...floats].reverse().find((f) => f.plane);
  return (
    <div data-testid={`tour-location-${loc.id}`} data-occupant={wrestler?.wrestler_id || ""}
         className="absolute z-10 flex flex-col items-center" style={{ left: `${loc.x}%`, top: `${loc.y}%`, width: "5.6%", transform: "translate(-50%,-50%)" }}
         onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
         onDrop={(e) => { e.preventDefault(); setOver(false); const wid = e.dataTransfer.getData("text/wrestler"); if (wid) onDrop(wid); }}>
      <button onClick={onClick} title={`${loc.name} · Lv ${loc.level}${loc.hit_zero ? " · Emptied today: levels up at daily reset" : ""}`}
              className={`relative w-full aspect-square transition-transform duration-200 hover:scale-110 ${over || armed ? "scale-110" : ""}`}
              style={{ filter: over ? "drop-shadow(0 0 14px #FFE600)" : active ? "drop-shadow(0 0 10px #39FF14)" : "drop-shadow(0 0 6px rgba(0,0,0,0.9))" }}>
        <img key={lastDrop?.key || "idle"} src={`/tour/${loc.icon}`} alt={loc.name} draggable={false}
             className={`w-full h-full object-contain ${armed && !wrestler ? "animate-pulse" : ""} ${lastDrop ? "tour-drop-bump" : ""}`} />
        {floats.filter((f) => f.plane).map((f) => (
          <span key={`ring-${f.key}`} className="tour-drop-ring absolute inset-0 m-auto w-full aspect-square rounded-full border-2 border-[#00F0FF] pointer-events-none" aria-hidden="true" />
        ))}
        {wrestler && <PlacedWrestler wrestler={wrestler} selected={selected} onDragStart={onDragStartPlaced} />}
        {floats.map((f) => (
          <span key={f.key} className={`tour-float absolute left-1/2 top-0 z-30 font-heading whitespace-nowrap pointer-events-none ${f.plane ? "text-[#00F0FF] text-sm" : f.active ? "text-[#39FF14] text-sm" : "text-[#C9BDF0] text-[10px]"}`}
                style={{ textShadow: f.plane ? "0 0 8px #00F0FF" : f.active ? "0 0 8px #39FF14" : "0 0 4px #000" }} data-testid={f.plane ? "tour-delivery-float" : undefined}>
            {f.plane ? (
              <span className="tour-drop-badge inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#0A051A]/90 border border-[#00F0FF]">
                <Plane className="w-3 h-3" /> DROP +{fmtAmt(f.currency, f.amount)}
              </span>
            ) : <>+{fmtAmt(f.currency, f.amount)}</>}
          </span>
        ))}
        <TourClaimPops gains={pops} className="-top-[70%]" />
      </button>
      <div className={`mt-1 w-[150%] text-center rounded-md bg-[#0A051A]/70 backdrop-blur-sm border border-white/10 px-1 py-0.5 shadow-[0_2px_8px_rgba(0,0,0,0.6)]`}
           data-testid={`tour-location-info-${loc.id}`}>
        <div className={`${inZoom ? "block" : "hidden lg:block"} font-heading text-[8px] uppercase tracking-wide leading-[1.15] text-white break-words`}>
          {loc.name} <span className="text-[#FFE600]">Lv{loc.level}</span>
        </div>
        <PoolBar loc={loc} inZoom={inZoom} />
      </div>
    </div>
  );
}
